/**
 * Bots: wire types for the Developer Portal, the versioned Bot API (/api/bot/v1), the bot gateway (the /bot
 * Socket.IO namespace) and slash commands, plus the validation both ends share.
 */
import { z } from 'zod';
import { componentListSchema, modalSchema, type MessageComponent, type ModalDefinition } from './components';
import { LIMITS } from './constants';
import { pollInputSchema, type BotPollEnded, type BotPollVote } from './polls';
import type { BotEventInterest, ScheduledEventDeleted, ScheduledEventPayload } from './scheduledEvents';
import { embedListSchema } from './embeds';
import { MENTION_PATTERNS } from './mentions';
import { ALL_PERMISSIONS, BOT_PERMISSIONS, MAX_PERMISSIONS, permWithin } from './permissions';
import type { Channel, Member, Message, PresenceStatus, PublicUser, RichEmbed, Role, Space, VoiceState } from './types';
import { displayNameSchema, idSchema, usernameSchema } from './validation';

export const BOT_LIMITS = {
  botsPerUser: 10,
  spacesPerBot: 100,
  descriptionMax: 400,
  /** Global commands per bot, and commands per bot in each space. */
  commands: 100,
  options: 25,
  choices: 25,
  /** Live gateway connections per bot. */
  connections: 5,
} as const;

/**
 * Words a bot's username or display name can't contain, so a bot can't pass itself off as Spawnwire or its staff.
 * Compared after lowercasing and dropping everything but letters and digits.
 */
export const BOT_RESERVED_WORDS = ['spawnwire', 'system', 'official', 'staff', 'admin', 'moderator'] as const;

export function isReservedBotName(name: string): boolean {
  const flat = name.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
  return BOT_RESERVED_WORDS.some((w) => flat.includes(w));
}

const reservedMessage = 'Bot names cannot contain Spawnwire, System, Official, Staff, Admin or Moderator';

export const botUsernameSchema = usernameSchema.refine((v) => !isReservedBotName(v), reservedMessage);
export const botDisplayNameSchema = displayNameSchema.refine((v) => !isReservedBotName(v), reservedMessage);
const permissionBits = z.number().int().min(0).max(MAX_PERMISSIONS);

export const botCreateSchema = z.object({
  username: botUsernameSchema,
  displayName: botDisplayNameSchema.optional(),
  description: z.string().trim().max(BOT_LIMITS.descriptionMax).default(''),
});

export const botUpdateSchema = z.object({
  username: botUsernameSchema.optional(),
  displayName: botDisplayNameSchema.optional(),
  description: z.string().trim().max(BOT_LIMITS.descriptionMax).optional(),
  /** Anyone who can manage a space may add a public bot; a private one only its owner. */
  public: z.boolean().optional(),
  /** What the bot asks for when added, unless an invite link says otherwise. Only BOT_PERMISSIONS count. */
  defaultPermissions: permissionBits.refine((p) => permWithin(p, BOT_PERMISSIONS), 'Bots can only ask for bot permissions').optional(),
});

export const botTokenResetSchema = z.object({ password: z.string().min(1).max(LIMITS.passwordMax) });

export const botAuthorizeSchema = z.object({
  spaceId: idSchema,
  permissions: permissionBits.refine((p) => permWithin(p, BOT_PERMISSIONS), 'Bots can only be given bot permissions'),
});

export const botAuthorizeQuerySchema = z.object({
  permissions: z.coerce.number().int().min(0).max(MAX_PERMISSIONS).optional(),
});

// ---- Slash commands -----------------------------------------------------------------

export const COMMAND_OPTION_TYPES = ['string', 'integer', 'number', 'boolean', 'user', 'channel', 'role'] as const;
export type CommandOptionType = (typeof COMMAND_OPTION_TYPES)[number];

/** Chat commands are typed after a slash; user and message commands are picked from a person's or a message's menu. */
export const COMMAND_TYPES = ['chat', 'user', 'message'] as const;
export type CommandType = (typeof COMMAND_TYPES)[number];

/** Lowercase letters, digits, - and _: what people type after the slash. */
export const COMMAND_NAME_PATTERN = /^[a-z0-9_-]{1,32}$/;
/** Names shown in a menu can have capitals and spaces, like "Report message". */
export const CONTEXT_COMMAND_NAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9 _-]{0,31}$/;

export interface CommandOptionChoice {
  name: string;
  value: string | number;
}

/** A value people give a command. */
export interface CommandOption {
  name: string;
  description: string;
  type: CommandOptionType;
  required: boolean;
  /** Only these values are accepted (string, integer and number options). */
  choices?: CommandOptionChoice[];
  /** The bot suggests values while people type (string, integer and number options; not with `choices`). */
  autocomplete?: boolean;
  /** Integer and number options. */
  minValue?: number;
  maxValue?: number;
  /** String options. */
  maxLength?: number;
}

/**
 * A subcommand (`/ban add`) holds options; a group (`/role member add`) holds subcommands. A command has either
 * options of its own or subcommands and groups, never both.
 */
export interface CommandBranch {
  name: string;
  description: string;
  type: 'subcommand' | 'subcommand_group';
  options: CommandNode[];
}

export type CommandNode = CommandOption | CommandBranch;

export function isBranch(node: CommandNode): node is CommandBranch {
  return node.type === 'subcommand' || node.type === 'subcommand_group';
}

export interface BotCommand {
  id: string;
  botId: string;
  /** Null for a global command, available in every space the bot is in. */
  spaceId: string | null;
  name: string;
  description: string;
  type: CommandType;
  /** Permissions a person needs in the channel to run it; null for anyone who can use commands there. */
  defaultMemberPermissions: number | null;
  options: CommandNode[];
  createdAt: string;
  updatedAt: string;
}

/** A command as people in a space see it: with the bot that answers it. */
export interface SpaceCommand extends BotCommand {
  bot: PublicUser;
}

const commandName = z.string().regex(COMMAND_NAME_PATTERN, 'Use 1-32 lowercase letters, digits, - or _');
const commandDescription = z.string().trim().min(1, 'Add a description').max(100);

export const commandOptionSchema = z
  .object({
    name: commandName,
    description: commandDescription,
    type: z.enum(COMMAND_OPTION_TYPES),
    required: z.boolean().default(false),
    choices: z
      .array(z.object({ name: z.string().trim().min(1).max(100), value: z.union([z.string().max(100), z.number().finite()]) }))
      .min(1)
      .max(BOT_LIMITS.choices)
      .optional(),
    autocomplete: z.boolean().optional(),
    minValue: z.number().finite().optional(),
    maxValue: z.number().finite().optional(),
    maxLength: z.number().int().min(1).max(LIMITS.messageLength).optional(),
  })
  .superRefine((o, c) => {
    const numeric = o.type === 'integer' || o.type === 'number';
    if (o.choices) {
      if (o.type !== 'string' && !numeric) c.addIssue({ code: 'custom', path: ['choices'], message: 'Only string, integer and number options can have choices' });
      for (const ch of o.choices) {
        const ok = o.type === 'string' ? typeof ch.value === 'string' : typeof ch.value === 'number' && (o.type === 'number' || Number.isInteger(ch.value));
        if (!ok) c.addIssue({ code: 'custom', path: ['choices'], message: `Choice values must be ${o.type === 'string' ? 'strings' : o.type === 'integer' ? 'integers' : 'numbers'}` });
      }
    }
    if (o.autocomplete) {
      if (o.type !== 'string' && !numeric) c.addIssue({ code: 'custom', path: ['autocomplete'], message: 'Only string, integer and number options can autocomplete' });
      if (o.choices) c.addIssue({ code: 'custom', path: ['autocomplete'], message: 'An option has fixed choices or autocomplete, not both' });
    }
    if ((o.minValue !== undefined || o.maxValue !== undefined) && !numeric) c.addIssue({ code: 'custom', path: ['minValue'], message: 'Only integer and number options have a minimum or maximum' });
    if (o.minValue !== undefined && o.maxValue !== undefined && o.minValue > o.maxValue) c.addIssue({ code: 'custom', path: ['minValue'], message: 'minValue is above maxValue' });
    if (o.maxLength !== undefined && o.type !== 'string') c.addIssue({ code: 'custom', path: ['maxLength'], message: 'Only string options have a maximum length' });
  });

/** Names are unique, and required options come first so people can type them in order. */
function checkOptionList(options: { name: string; required?: boolean }[], c: z.RefinementCtx, path: (string | number)[]): void {
  const names = new Set<string>();
  let optionalSeen = false;
  options.forEach((o, i) => {
    if (names.has(o.name)) c.addIssue({ code: 'custom', path: [...path, i, 'name'], message: `Two options are called "${o.name}"` });
    names.add(o.name);
    if (o.required && optionalSeen) c.addIssue({ code: 'custom', path: [...path, i, 'required'], message: 'Required options must come before optional ones' });
    if (!o.required) optionalSeen = true;
  });
}

export const subcommandSchema = z
  .object({
    type: z.literal('subcommand'),
    name: commandName,
    description: commandDescription,
    options: z.array(commandOptionSchema).max(BOT_LIMITS.options).default([]),
  })
  .superRefine((s, c) => checkOptionList(s.options, c, ['options']));

export const subcommandGroupSchema = z
  .object({
    type: z.literal('subcommand_group'),
    name: commandName,
    description: commandDescription,
    options: z.array(subcommandSchema).min(1, 'A group needs at least one subcommand').max(BOT_LIMITS.options),
  })
  .superRefine((g, c) => {
    const names = new Set<string>();
    g.options.forEach((s, i) => {
      if (names.has(s.name)) c.addIssue({ code: 'custom', path: ['options', i, 'name'], message: `Two subcommands are called "${s.name}"` });
      names.add(s.name);
    });
  });

const commandNodeSchema = z.union([commandOptionSchema, subcommandSchema, subcommandGroupSchema]);

export const commandSchema = z
  .object({
    type: z.enum(COMMAND_TYPES).default('chat'),
    name: z.string(),
    description: z.string().trim().max(100).default(''),
    options: z.array(commandNodeSchema).max(BOT_LIMITS.options).default([]),
    /** Permission bits a person needs in the channel to run the command; the server checks them on every run. */
    defaultMemberPermissions: z
      .number()
      .int()
      .min(0)
      .max(MAX_PERMISSIONS)
      .refine((p) => permWithin(p, ALL_PERMISSIONS), 'That permission does not exist')
      .nullable()
      .optional(),
  })
  .superRefine((cmd, c) => {
    if (cmd.type === 'chat') {
      if (!COMMAND_NAME_PATTERN.test(cmd.name)) c.addIssue({ code: 'custom', path: ['name'], message: 'Use 1-32 lowercase letters, digits, - or _' });
      if (!cmd.description) c.addIssue({ code: 'custom', path: ['description'], message: 'Add a description' });
      const branches = cmd.options.filter((o) => o.type === 'subcommand' || o.type === 'subcommand_group');
      if (branches.length > 0 && branches.length !== cmd.options.length) {
        c.addIssue({ code: 'custom', path: ['options'], message: 'A command has either options or subcommands, not both' });
      } else if (branches.length > 0) {
        const names = new Set<string>();
        cmd.options.forEach((o, i) => {
          if (names.has(o.name)) c.addIssue({ code: 'custom', path: ['options', i, 'name'], message: `Two subcommands or groups are called "${o.name}"` });
          names.add(o.name);
        });
      } else {
        checkOptionList(cmd.options as { name: string; required?: boolean }[], c, ['options']);
      }
    } else {
      if (!CONTEXT_COMMAND_NAME_PATTERN.test(cmd.name)) c.addIssue({ code: 'custom', path: ['name'], message: 'Use 1-32 letters, digits, spaces, - or _' });
      if (cmd.options.length) c.addIssue({ code: 'custom', path: ['options'], message: 'User and message commands take no options' });
    }
  });

/** A full set of commands: registering replaces whatever was there before. */
export const commandListSchema = z
  .array(commandSchema)
  .max(BOT_LIMITS.commands)
  .superRefine((list, c) => {
    const names = new Set<string>();
    list.forEach((cmd, i) => {
      const key = cmd.name.toLowerCase();
      if (names.has(key)) c.addIssue({ code: 'custom', path: [i, 'name'], message: `Two commands are called "${cmd.name}"` });
      names.add(key);
    });
  });

export type CommandInput = z.input<typeof commandSchema>;
/** A command after validation, with defaults filled in. */
export type CommandData = z.output<typeof commandSchema>;
export type OptionValue = string | number | boolean;

const optionValues = z.record(z.string().max(32), z.union([z.string().max(LIMITS.messageLength), z.number().finite(), z.boolean()])).default({});
const subcommandPath = z.array(z.string().max(32)).max(2).default([]);

/**
 * Running a command: option values by name (users, channels and roles by id), the names of the subcommand group
 * and subcommand when it has them, and, for user and message commands, whose menu it was picked from.
 */
export const interactionCreateSchema = z.object({
  commandId: idSchema,
  subcommand: subcommandPath,
  options: optionValues,
  targetId: idSchema.optional(),
});

/** Asking a bot for suggestions while someone types a value: the option, what they typed, and the other values so far. */
export const autocompleteQuerySchema = z.object({
  commandId: idSchema,
  subcommand: subcommandPath,
  option: z.string().max(32),
  value: z.string().max(100).default(''),
  options: optionValues,
});

export const AUTOCOMPLETE_LIMITS = { choices: 25, name: 100, value: 100 } as const;

/** The bot's suggestions: up to 25, each shown by `name` and sent as `value` (a number for integer and number options). */
export const autocompleteChoicesSchema = z
  .array(z.object({ name: z.string().trim().min(1).max(AUTOCOMPLETE_LIMITS.name), value: z.union([z.string().max(AUTOCOMPLETE_LIMITS.value), z.number().finite()]) }))
  .max(AUTOCOMPLETE_LIMITS.choices);

/**
 * Follows what was typed after a command's name down through its subcommand groups and subcommands. `options` are
 * the options to fill in at the end of the path; `next` lists the subcommands or groups still to pick between
 * (when the path isn't complete yet); `rest` is what is left to read as values.
 */
export function resolveSubcommand(command: Pick<BotCommand, 'options'>, text: string): { path: string[]; options: CommandOption[]; next: CommandBranch[] | null; rest: string } {
  let level: CommandNode[] = command.options;
  const path: string[] = [];
  let rest = text.trimStart();
  while (level.length > 0 && level.every(isBranch)) {
    const branches = level as CommandBranch[];
    // A name typed in full at the very end counts as chosen, so running `/role list` needs no trailing space.
    const m = /^(\S+)(?:\s+|$)/.exec(rest);
    const found = m ? branches.find((b) => b.name === m[1]) : undefined;
    if (!m || !found) return { path, options: [], next: branches, rest };
    path.push(found.name);
    level = found.options;
    rest = rest.slice(m[0].length);
  }
  return { path, options: level as CommandOption[], next: null, rest };
}

/** The options to fill in for a path of subcommand names (or none, for a command without subcommands). */
export function leafOptions(command: Pick<BotCommand, 'options'>, path: readonly string[]): { options: CommandOption[] } | { error: string } {
  let level: CommandNode[] = command.options;
  for (const name of path) {
    const found = level.find((n) => isBranch(n) && n.name === name);
    if (!found || !isBranch(found)) return { error: `There is no subcommand "${name}".` };
    level = found.options;
  }
  if (level.length > 0 && level.every(isBranch)) return { error: 'Pick a subcommand.' };
  return { options: level as CommandOption[] };
}

/**
 * Checks option values against a command's definition and converts them to their types. Ids are only checked
 * for shape here; the server checks that the user, channel or role exists in the space.
 */
export function checkCommandOptions(command: { options: readonly CommandOption[] }, raw: Record<string, OptionValue>): { values: Record<string, OptionValue> } | { error: string } {
  const known = new Set(command.options.map((o) => o.name));
  for (const key of Object.keys(raw)) if (!known.has(key)) return { error: `Unknown option "${key}".` };
  const values: Record<string, OptionValue> = {};
  for (const o of command.options) {
    const v = raw[o.name];
    if (v === undefined || v === '') {
      if (o.required) return { error: `"${o.name}" is required.` };
      continue;
    }
    let value: OptionValue;
    switch (o.type) {
      case 'string':
        if (typeof v !== 'string') return { error: `"${o.name}" must be text.` };
        if (v.length > (o.maxLength ?? LIMITS.messageLength)) return { error: `"${o.name}" can be at most ${o.maxLength ?? LIMITS.messageLength} characters.` };
        value = v;
        break;
      case 'integer':
      case 'number': {
        const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
        if (!Number.isFinite(n) || (o.type === 'integer' && !Number.isInteger(n))) return { error: `"${o.name}" must be ${o.type === 'integer' ? 'a whole number' : 'a number'}.` };
        if (o.minValue !== undefined && n < o.minValue) return { error: `"${o.name}" must be at least ${o.minValue}.` };
        if (o.maxValue !== undefined && n > o.maxValue) return { error: `"${o.name}" must be at most ${o.maxValue}.` };
        value = n;
        break;
      }
      case 'boolean': {
        const s = typeof v === 'string' ? v.toLowerCase() : v;
        if (s === true || s === 'true' || s === 'yes') value = true;
        else if (s === false || s === 'false' || s === 'no') value = false;
        else return { error: `"${o.name}" must be true or false.` };
        break;
      }
      case 'user':
      case 'channel':
      case 'role':
        if (typeof v !== 'string' || !idSchema.safeParse(v).success) return { error: `"${o.name}" must be a ${o.type}.` };
        value = v;
        break;
    }
    if (o.choices && !o.choices.some((c) => c.value === value)) return { error: `"${o.name}" must be one of: ${o.choices.map((c) => c.name).join(', ')}.` };
    values[o.name] = value;
  }
  return { values };
}

/** Splits what was typed after a command's name into values: `name:value` pairs and values in order. */
export function parseCommandArguments(command: { options: readonly CommandOption[] }, text: string): { values: Record<string, string> } | { error: string } {
  const values: Record<string, string> = {};
  const unfilled = () => command.options.filter((o) => values[o.name] === undefined);
  /** Where another option given by name starts further on (like ` count:3`), or -1. */
  const namedAhead = (s: string, except: string) => {
    const names = unfilled()
      .filter((o) => o.name !== except)
      .map((o) => o.name);
    if (names.length === 0) return -1;
    const m = new RegExp(`\\s(?:${names.join('|')}):`).exec(s);
    return m ? m.index : -1;
  };
  let rest = text.trim();
  while (rest) {
    const named = /^([a-z0-9_-]{1,32}):/.exec(rest);
    const byName = named ? command.options.find((o) => o.name === named[1]) : undefined;
    let option: CommandOption | undefined;
    if (byName) {
      option = byName;
      rest = rest.slice(named![0].length).trimStart();
    } else {
      option = unfilled()[0];
      if (!option) return { error: 'Too many values for this command.' };
    }
    if (values[option.name] !== undefined) return { error: `"${option.name}" is given twice.` };
    let value: string;
    if (rest.startsWith('"')) {
      const end = rest.indexOf('"', 1);
      if (end === -1) return { error: 'A quote is not closed.' };
      value = rest.slice(1, end);
      rest = rest.slice(end + 1).trimStart();
    } else if (!byName && option.type === 'string' && unfilled().filter((o) => o.required).length <= 1) {
      // The last text value takes the rest of the line (up to another option given by name), so
      // `/say hello there loud:true` needs no quotes.
      const cut = namedAhead(rest, option.name);
      value = cut === -1 ? rest : rest.slice(0, cut);
      rest = cut === -1 ? '' : rest.slice(cut).trimStart();
    } else {
      const space = rest.search(/\s/);
      value = space === -1 ? rest : rest.slice(0, space);
      rest = space === -1 ? '' : rest.slice(space).trimStart();
    }
    values[option.name] = option.type === 'user' || option.type === 'channel' || option.type === 'role' ? mentionId(option.type, value) : value;
  }
  return { values };
}

/** `<@id>`, `<#id>` or `<@&id>` (what mentions turn into before sending) to the bare id. */
function mentionId(type: 'user' | 'channel' | 'role', value: string): string {
  const re = new RegExp(`^${(type === 'user' ? MENTION_PATTERNS.user : type === 'channel' ? MENTION_PATTERNS.channel : MENTION_PATTERNS.role).source}$`);
  return re.exec(value)?.[1] ?? value;
}

// ---- Interactions -------------------------------------------------------------------

export type InteractionStatus = 'pending' | 'deferred' | 'completed' | 'failed';

/**
 * What started an interaction: a command (typed, or picked from a person's or a message's menu), a click on a
 * message component, a submitted modal form, or someone typing a value that asks the bot for suggestions.
 */
export type InteractionType = 'command' | 'component' | 'modal' | 'autocomplete';

/** Persisted interactions; suggestions are asked for and answered within a moment and aren't kept. */
export type StoredInteractionType = Exclude<InteractionType, 'autocomplete'>;

export interface InteractionOption {
  name: string;
  type: CommandOptionType;
  value: OptionValue;
}

/** A slash command run, as the bot receives it in `interaction.created`. */
export interface BotInteraction {
  id: string;
  type: InteractionType;
  /** The command that was run; null for clicks and form submissions. */
  command: { id: string; name: string; spaceId: string | null; type: CommandType } | null;
  /** The subcommand group and subcommand that were run, when the command has them. */
  subcommandGroup?: string | null;
  subcommand?: string | null;
  /** The options given (for an autocomplete request, the ones filled in so far). */
  options: InteractionOption[];
  /** For user and message commands: the person or message whose menu it was picked from. */
  target?: { type: 'user'; user: PublicUser; member: Member | null } | { type: 'message'; message: BotMessage } | null;
  /** `type: 'autocomplete'`: the option being typed, what has been typed, and what to answer with (`type: 'autocomplete'` callback). */
  autocomplete?: { option: string; type: CommandOptionType; value: string } | null;
  /** Full objects for user, channel and role options. */
  resolved: {
    users: Record<string, PublicUser>;
    members: Record<string, Member>;
    channels: Record<string, Channel>;
    roles: Record<string, Role>;
  };
  spaceId: string;
  channelId: string;
  /** Who ran it. */
  user: PublicUser;
  member: Member | null;
  createdAt: string;
  /** Reply or defer before this, or the person is told the bot did not respond. */
  respondBy: string;
  /** A deferred command must be finished (with a follow-up) before this. */
  expiresAt: string;
  /** A click on a button or choice in a select menu (`type: 'component'`). */
  component?: { customId: string; type: MessageComponent['type']; values: string[] } | null;
  /** The message the component is on (`type: 'component'`), or that the form was opened from. */
  message?: BotMessage | null;
  /** A submitted form (`type: 'modal'`): the modal's customId and what was typed, by field customId. */
  modal?: { customId: string; values: Record<string, string> } | null;
}

/** What the person who ran a command sees of it while it runs, or when only they can see the reply. */
export interface InteractionState {
  id: string;
  channelId: string;
  botId: string;
  type: StoredInteractionType;
  /** The command's name; empty for clicks and form submissions. */
  commandName: string;
  /** Typed after a slash (`chat`), or picked from a person's or a message's menu; null for clicks and forms. */
  commandType: CommandType | null;
  status: InteractionStatus;
  error: string | null;
  /** A reply only the person who ran the command can see; it is not kept. */
  ephemeral: { content: string; embeds?: RichEmbed[]; createdAt: string } | null;
  /** A form the bot opened for this person to fill in; submit it with POST /interactions/:id/modal. */
  modal: ModalDefinition | null;
  createdAt: string;
  respondBy: string;
  expiresAt: string;
}

/** What a bot's message carries: text, embeds and files (uploaded first, then given by id). */
export const botMessageBody = {
  content: z.string().trim().max(LIMITS.messageLength, `Messages can have at most ${LIMITS.messageLength} characters`).default(''),
  embeds: embedListSchema.optional(),
  attachmentIds: z.array(idSchema).max(LIMITS.attachmentsPerMessage).optional(),
  components: componentListSchema.optional(),
  poll: pollInputSchema.optional(),
};

type BotMessageBody = { content: string; embeds?: { image?: unknown; thumbnail?: unknown }[]; attachmentIds?: string[]; components?: unknown[]; poll?: unknown; ephemeral?: boolean };

/** A message needs something in it; an answer only one person sees isn't kept, so it can't hold files. */
function checkBody(body: BotMessageBody, c: z.RefinementCtx): void {
  if (!body.content && !body.embeds?.length && !body.attachmentIds?.length && !body.poll) c.addIssue({ code: 'custom', path: ['content'], message: 'Messages need content, an embed, a file or a poll' });
  if (body.ephemeral && body.poll) c.addIssue({ code: 'custom', path: ['poll'], message: 'Ephemeral replies cannot have a poll' });
  if (body.ephemeral && body.attachmentIds?.length) c.addIssue({ code: 'custom', path: ['attachmentIds'], message: 'Ephemeral replies cannot have files' });
  if (body.ephemeral && body.components?.length) c.addIssue({ code: 'custom', path: ['components'], message: 'Ephemeral replies cannot have components' });
  if (body.ephemeral && body.embeds?.some((e) => e.image || e.thumbnail)) c.addIssue({ code: 'custom', path: ['embeds'], message: 'Ephemeral replies cannot have embed images' });
}

function editContent() {
  return z.string().trim().max(LIMITS.messageLength, `Messages can have at most ${LIMITS.messageLength} characters`).optional();
}

export const botMessageCreateSchema = z.object({ ...botMessageBody, replyToId: idSchema.optional() }).superRefine(checkBody);

/** Editing replaces what is given: leave `embeds` or `components` out to keep them, or send `[]` to remove them. */
export const botMessageEditSchema = z
  .object({ content: editContent(), embeds: embedListSchema.optional(), components: componentListSchema.optional() })
  .refine((b) => b.content !== undefined || b.embeds !== undefined || b.components !== undefined, 'Send content, embeds or components to change');

export const interactionCallbackSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('reply'), ...botMessageBody, ephemeral: z.boolean().default(false) }).superRefine(checkBody),
  z.object({ type: z.literal('defer') }),
  /** A click: change the message the component is on. */
  z
    .object({ type: z.literal('update'), content: editContent(), embeds: embedListSchema.optional(), components: componentListSchema.optional() })
    .refine((b) => b.content !== undefined || b.embeds !== undefined || b.components !== undefined, 'Send content, embeds or components to change'),
  /** A click: acknowledge it and change nothing. */
  z.object({ type: z.literal('defer_update') }),
  /** Suggestions for an `autocomplete` interaction. Nothing is posted; the person sees the choices. */
  z.object({ type: z.literal('autocomplete'), choices: autocompleteChoicesSchema }),
  /** Open a form for the person to fill in; they submit it, and the bot gets a `modal` interaction. */
  z.object({ type: z.literal('modal'), modal: modalSchema }),
]);

export const interactionFollowupSchema = z.object({ ...botMessageBody, ephemeral: z.boolean().default(false) }).superRefine(checkBody);

// ---- Developer Portal --------------------------------------------------------------

export type BotAuditAction = 'bot_create' | 'bot_update' | 'avatar_update' | 'token_reset' | 'space_add' | 'space_remove' | 'commands_update';

export interface BotStats {
  spaces: number;
  /** People in those spaces, counted once per space. */
  members: number;
  messagesSent: number;
  messagesSent7d: number;
  commands: number;
  commandsRun: number;
  commandsRun7d: number;
  commandsFailed: number;
  online: boolean;
  connections: number;
  lastConnectedAt: string | null;
}

export interface BotSummary {
  id: string;
  user: PublicUser;
  public: boolean;
  defaultPermissions: number;
  createdAt: string;
  tokenResetAt: string;
  online: boolean;
  spaceCount: number;
}

export interface BotDetail extends BotSummary {
  stats: BotStats;
}

/** A new bot, or a reset token: the token is shown this once and never again. */
export interface BotTokenResult {
  bot: BotDetail;
  token: string;
}

export interface BotActivityEntry {
  id: string;
  action: BotAuditAction;
  actor: PublicUser | null;
  details: Record<string, unknown>;
  createdAt: string;
}

/** The consent screen: the bot, what it asks for, and the spaces the viewer could add it to. */
export interface BotAuthorizeInfo {
  bot: PublicUser;
  requested: number;
  spaces: {
    id: string;
    name: string;
    iconUrl: string | null;
    /** The permissions the viewer could give it there: what it asks for and the viewer has. */
    grantable: number;
    /** The bot is already a member. */
    added: boolean;
    banned: boolean;
  }[];
}

/** A bot in a space, as Space settings → Bots lists it. */
export interface SpaceBot {
  bot: PublicUser;
  addedBy: PublicUser | null;
  addedAt: string;
  /** The role made for it when it was added, and what that role allows. */
  roleId: string | null;
  permissions: number;
  online: boolean;
}

// ---- Bot API and gateway -----------------------------------------------------------

/**
 * Messages as bots see them. Gateway events leave out `me` on reactions; REST responses fill it in. Bots only ever see
 * messages in spaces, so `spaceId` is always set.
 */
export type BotMessage = Omit<Message, 'nonce' | 'reactions' | 'spaceId'> & { spaceId: string; reactions: { emoji: string; count: number; me?: boolean }[] };

export interface BotApplication {
  id: string;
  user: PublicUser;
  ownerId: string;
  public: boolean;
  defaultPermissions: number;
}

export interface BotReadyPayload {
  user: PublicUser;
  application: { id: string; ownerId: string; public: boolean };
  /** Every space the bot is in, with only the channels it can see. */
  spaces: Space[];
  /**
   * The gateway session. Keep `id` and the `seq` of the last event you handled, and send them as `auth.session`
   * and `auth.seq` when you reconnect to be sent what you missed. `resumed: false` means you were not caught up
   * (new connection, or too much time or too many events passed): read what you need again through the REST API.
   */
  session?: { id: string; seq: number; resumed: boolean };
}

/** The last argument of every gateway event except `typing.started` and `presence.updated`. */
export interface BotEventMeta {
  /** 1, 2, 3, ... per session, with no gaps. */
  seq: number;
  /** Set on events sent again after a reconnect. */
  replayed?: boolean;
}

export interface BotReactionEvent {
  messageId: string;
  channelId: string;
  spaceId: string;
  userId: string;
  emoji: string;
  reactions: { emoji: string; count: number }[];
}

/** Someone's voice state as bots see it: without the connection's session id. */
export type BotVoiceState = Omit<VoiceState, 'sessionId'>;

export interface BotServerToClientEvents {
  ready: (payload: BotReadyPayload) => void;
  'message.created': (message: BotMessage) => void;
  'message.updated': (message: BotMessage) => void;
  'message.deleted': (payload: { id: string; channelId: string; spaceId: string }) => void;
  /** A message in an announcement channel was published to everyone who can see the channel. */
  'message.published': (message: BotMessage) => void;
  /** Someone chose an answer on a poll in a channel the bot can see. */
  'poll.vote_added': (vote: BotPollVote) => void;
  /** Someone took their choice back, or changed it. */
  'poll.vote_removed': (vote: BotPollVote) => void;
  /** A poll closed: its time ran out, or it was ended. */
  'poll.ended': (poll: BotPollEnded) => void;
  'reaction.added': (payload: BotReactionEvent) => void;
  'reaction.removed': (payload: BotReactionEvent) => void;
  'typing.started': (payload: { channelId: string; spaceId: string; userId: string; at: number }) => void;
  /** Someone the bot shares a space with went online, idle, busy or offline. */
  'presence.updated': (payload: { userId: string; status: PresenceStatus }) => void;
  /** Someone joined, changed or left (`state: null`) a voice or stage channel the bot can see. */
  'voice.state_updated': (payload: { channelId: string; spaceId: string; userId: string; state: BotVoiceState | null }) => void;
  'member.joined': (member: Member) => void;
  'member.updated': (member: Member) => void;
  'member.left': (payload: { spaceId: string; userId: string }) => void;
  'member.kicked': (payload: { spaceId: string; userId: string }) => void;
  'member.banned': (payload: { spaceId: string; user: PublicUser; reason: string }) => void;
  'member.unbanned': (payload: { spaceId: string; user: PublicUser }) => void;
  'channel.created': (channel: Channel) => void;
  'channel.updated': (channel: Channel) => void;
  'channel.deleted': (payload: { id: string; spaceId: string }) => void;
  'role.created': (role: Role) => void;
  'role.updated': (role: Role) => void;
  'role.deleted': (payload: { id: string; spaceId: string }) => void;
  /** A scheduled event was created in a space the bot is in. Every member sees every event, so bots do too. */
  'event.created': (event: ScheduledEventPayload) => void;
  /** An event changed: details, or it started, ended or was canceled (see `status`). */
  'event.updated': (event: ScheduledEventPayload) => void;
  'event.deleted': (payload: ScheduledEventDeleted) => void;
  /** Someone said they are interested in an event. */
  'event.interested_added': (payload: BotEventInterest) => void;
  /** Someone took it back. */
  'event.interested_removed': (payload: BotEventInterest) => void;
  /** You joined a space, or what you can see in it changed: replace your copy. */
  'space.available': (space: Space) => void;
  'space.updated': (space: Omit<Space, 'channels' | 'roles'>) => void;
  'space.left': (payload: { id: string; reason: 'deleted' | 'left' | 'kicked' | 'banned' }) => void;
  'interaction.created': (interaction: BotInteraction) => void;
}

/** Bots only listen on the gateway; everything they do goes through the REST API. */
export type BotClientToServerEvents = Record<string, never>;

export type BotEventName = keyof BotServerToClientEvents;
